import { useState, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';

interface MenuItem {
  id: number;
  label: string;
  path: string;
  icon: string;
  enabled: boolean;
  order: number;
  parent: 'main' | 'footer' | 'mobile';
  badge?: string;
}

const initialMenuItems: MenuItem[] = [
  { id: 1, label: 'Home', path: '/', icon: 'ri-home-line', enabled: true, order: 1, parent: 'main' },
  { id: 2, label: 'Listings', path: '/listings', icon: 'ri-building-line', enabled: true, order: 2, parent: 'main' },
  { id: 3, label: 'Services', path: '/services', icon: 'ri-service-line', enabled: true, order: 3, parent: 'main' },
  { id: 4, label: 'How It Works', path: '/how-it-works', icon: 'ri-question-line', enabled: true, order: 4, parent: 'main' },
  { id: 5, label: 'AI Technology', path: '/ai-technology', icon: 'ri-brain-line', enabled: true, order: 5, parent: 'main' },
  { id: 6, label: 'Pricing', path: '/pricing', icon: 'ri-price-tag-3-line', enabled: true, order: 6, parent: 'main' },
  { id: 7, label: 'Case Studies', path: '/case-studies', icon: 'ri-file-list-3-line', enabled: true, order: 7, parent: 'main' },
  { id: 8, label: 'About', path: '/about', icon: 'ri-information-line', enabled: true, order: 8, parent: 'main' },
  { id: 9, label: 'Contact', path: '/contact', icon: 'ri-phone-line', enabled: true, order: 9, parent: 'main' },
  { id: 10, label: 'FAQ', path: '/faq', icon: 'ri-question-answer-line', enabled: true, order: 10, parent: 'main' },
  { id: 11, label: 'Press', path: '/press', icon: 'ri-newspaper-line', enabled: false, order: 11, parent: 'main' },
  { id: 12, label: 'Visionary', path: '/visionary', icon: 'ri-eye-line', enabled: false, order: 12, parent: 'main' },
  { id: 13, label: 'Strategies', path: '/strategies', icon: 'ri-map-2-line', enabled: false, order: 13, parent: 'main' },
  { id: 14, label: 'Partners', path: '/partners', icon: 'ri-shake-hands-line', enabled: true, order: 14, parent: 'footer' },
  { id: 15, label: 'Feasibility Report', path: '/feasibility-report', icon: 'ri-file-chart-line', enabled: true, order: 15, parent: 'footer', badge: 'AI' },
  { id: 16, label: 'Members Login', path: '/members', icon: 'ri-user-line', enabled: true, order: 16, parent: 'footer' },
];

type NavSection = 'all' | 'main' | 'footer' | 'mobile';

const sectionLabels: Record<NavSection, string> = {
  all: 'All Menus',
  main: 'Main Navigation',
  footer: 'Footer Navigation',
  mobile: 'Mobile Menu',
};

export default function MenuSettingsPage() {
  const [items, setItems] = useState<MenuItem[]>(initialMenuItems);
  const [filterSection, setFilterSection] = useState<NavSection>('all');
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [editForm, setEditForm] = useState({ label: '', path: '', badge: '' });
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [hasUnsaved, setHasUnsaved] = useState(false);

  const clearMsg = () => setTimeout(() => setActionMsg(null), 3000);

  const filtered = filterSection === 'all'
    ? items.sort((a, b) => a.parent.localeCompare(b.parent) || a.order - b.order)
    : items.filter(item => item.parent === filterSection).sort((a, b) => a.order - b.order);

  const mainVisible = items.filter(i => i.parent === 'main' && i.enabled).length;
  const footerVisible = items.filter(i => i.parent === 'footer' && i.enabled).length;
  const totalEnabled = items.filter(i => i.enabled).length;

  const toggleEnabled = useCallback((id: number) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, enabled: !item.enabled } : item));
    setHasUnsaved(true);
  }, []);

  const openEdit = (item: MenuItem) => {
    setEditingItem(item);
    setEditForm({ label: item.label, path: item.path, badge: item.badge || '' });
  };

  const saveEdit = useCallback(() => {
    if (!editingItem) return;
    if (!editForm.label.trim() || !editForm.path.trim()) {
      setActionMsg({ type: 'error', text: 'Label and path are required.' });
      clearMsg();
      return;
    }
    setItems(prev => prev.map(item =>
      item.id === editingItem.id
        ? { ...item, label: editForm.label.trim(), path: editForm.path.trim(), badge: editForm.badge.trim() || undefined }
        : item
    ));
    setEditingItem(null);
    setHasUnsaved(true);
    setActionMsg({ type: 'success', text: 'Menu item updated.' });
    clearMsg();
  }, [editingItem, editForm]);

  const moveItem = useCallback((id: number, direction: 'up' | 'down') => {
    setItems(prev => {
      const sorted = [...prev].sort((a, b) => a.parent.localeCompare(b.parent) || a.order - b.order);
      const idx = sorted.findIndex(i => i.id === id);
      if (idx < 0) return prev;
      const item = sorted[idx];
      const siblingIdx = direction === 'up' ? idx - 1 : idx + 1;
      const sibling = sorted[siblingIdx];
      if (!sibling || sibling.parent !== item.parent) return prev;

      // Swap orders
      const itemOrder = item.order;
      const siblingOrder = sibling.order;
      return prev.map(i => {
        if (i.id === id) return { ...i, order: siblingOrder };
        if (i.id === sibling.id) return { ...i, order: itemOrder };
        return i;
      });
    });
    setHasUnsaved(true);
  }, []);

  const handleSaveAll = () => {
    setActionMsg({ type: 'success', text: 'Menu configuration saved successfully!' });
    setHasUnsaved(false);
    clearMsg();
  };

  const handleReset = () => {
    setItems(initialMenuItems);
    setHasUnsaved(false);
    setActionMsg({ type: 'success', text: 'Menu reset to default configuration.' });
    clearMsg();
  };

  const parentBadge = (parent: string) => {
    const map: Record<string, string> = {
      main: 'bg-primary-100 text-primary-700',
      footer: 'bg-secondary-100 text-secondary-700',
      mobile: 'bg-accent-100 text-accent-700',
    };
    return map[parent] || 'bg-background-200/50 text-foreground-500';
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-4xl">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
            {actionMsg.text}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">Menu Settings</h1>
            <p className="text-xs text-foreground-500">{totalEnabled} items enabled across all navigation areas</p>
          </div>
          <div className="flex items-center gap-2">
            {hasUnsaved && (
              <span className="flex items-center gap-1.5 text-xs text-accent-700 bg-accent-100 px-3 py-1.5 rounded-lg border border-accent-200">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                Unsaved changes
              </span>
            )}
            <button onClick={handleReset} className="px-4 py-2.5 rounded-xl border border-background-200/60 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">
              <i className="ri-refresh-line mr-1" /> Reset
            </button>
            <button onClick={handleSaveAll} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">
              <i className="ri-save-line" /> Save Changes
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Main Nav', count: mainVisible, total: items.filter(i => i.parent === 'main').length, color: 'primary' },
            { label: 'Footer Nav', count: footerVisible, total: items.filter(i => i.parent === 'footer').length, color: 'secondary' },
            { label: 'Total Enabled', count: totalEnabled, total: items.length, color: 'accent' },
          ].map(stat => (
            <div key={stat.label} className="rounded-xl border bg-white border-background-200/60 p-4">
              <p className="text-[10px] text-foreground-500 uppercase tracking-wider">{stat.label}</p>
              <p className="font-heading text-lg font-bold text-foreground-950 mt-1">{stat.count} / {stat.total}</p>
              <div className="mt-2 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${stat.color === 'primary' ? 'bg-primary-500' : stat.color === 'secondary' ? 'bg-secondary-500' : 'bg-accent-500'}`} style={{ width: `${Math.round((stat.count / stat.total) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>

        {/* Section filter */}
        <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1 w-fit">
          {(['all', 'main', 'footer'] as NavSection[]).map(section => (
            <button
              key={section}
              onClick={() => setFilterSection(section)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm whitespace-nowrap cursor-pointer transition-all ${filterSection === section ? 'bg-accent-100 text-accent-700 font-semibold' : 'text-foreground-500 hover:text-foreground-950'}`}
            >
              {sectionLabels[section]}
            </button>
          ))}
        </div>

        {/* Menu Items List */}
        <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-background-200/60 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground-950">
              {filterSection === 'all' ? 'All Menu Items' : sectionLabels[filterSection]}
            </h3>
            <p className="text-xs text-foreground-500">{filtered.length} items · Drag rows to reorder</p>
          </div>
          <div className="divide-y divide-background-200/50">
            {filtered.map((item, idx) => {
              const sectionItems = items.filter(i => i.parent === item.parent).sort((a, b) => a.order - b.order);
              const itemIdxInSection = sectionItems.findIndex(i => i.id === item.id);
              const isFirst = itemIdxInSection === 0;
              const isLast = itemIdxInSection === sectionItems.length - 1;

              return (
                <div key={item.id} className={`flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-background-50 ${!item.enabled ? 'opacity-50' : ''}`}>
                  {/* Drag handle */}
                  <div className="flex flex-col gap-0.5 cursor-move flex-shrink-0">
                    <i className="ri-draggable text-foreground-400 text-base" />
                  </div>

                  {/* Order controls */}
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button onClick={() => moveItem(item.id, 'up')} disabled={isFirst} className="w-5 h-4 flex items-center justify-center text-foreground-400 hover:text-foreground-950 disabled:opacity-20 cursor-pointer">
                      <i className="ri-arrow-up-s-line text-xs" />
                    </button>
                    <button onClick={() => moveItem(item.id, 'down')} disabled={isLast} className="w-5 h-4 flex items-center justify-center text-foreground-400 hover:text-foreground-950 disabled:opacity-20 cursor-pointer">
                      <i className="ri-arrow-down-s-line text-xs" />
                    </button>
                  </div>

                  {/* Icon */}
                  <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-foreground-500 text-sm`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground-950">{item.label}</p>
                      {item.badge && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700">{item.badge}</span>
                      )}
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${parentBadge(item.parent)}`}>{item.parent}</span>
                    </div>
                    <p className="text-[11px] text-foreground-500">{item.path}</p>
                  </div>

                  {/* Toggle */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-foreground-500">{item.enabled ? 'Visible' : 'Hidden'}</span>
                    <button
                      onClick={() => toggleEnabled(item.id)}
                      className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${item.enabled ? 'bg-accent-500' : 'bg-background-200/60'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${item.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {/* Edit button */}
                  <button
                    onClick={() => openEdit(item)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-accent-700 cursor-pointer flex-shrink-0"
                    title="Edit"
                  >
                    <i className="ri-edit-line" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Preview */}
        <div className="rounded-xl border border-background-200/60 bg-white p-5">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">Navigation Preview</h3>
          <div className="flex flex-wrap gap-2">
            {items.filter(i => i.parent === 'main' && i.enabled).sort((a, b) => a.order - b.order).map(item => (
              <div key={item.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-sm text-foreground-950">
                <i className={`${item.icon} text-foreground-500 text-xs`} />
                {item.label}
                {item.badge && <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-accent-100 text-accent-700">{item.badge}</span>}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-foreground-500 mt-3">^ This is how your main navigation will appear to website visitors</p>
        </div>
      </div>

      {/* Edit Item Modal */}
      {editingItem && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditingItem(null)} />
          <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950">Edit Menu Item</h2>
              <button onClick={() => setEditingItem(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Label</label>
                <input type="text" value={editForm.label} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Path / URL</label>
                <input type="text" value={editForm.path} onChange={e => setEditForm(f => ({ ...f, path: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Badge Text (optional)</label>
                <input type="text" placeholder="e.g. New, AI, Beta" value={editForm.badge} onChange={e => setEditForm(f => ({ ...f, badge: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditingItem(null)} className="flex-1 py-2.5 rounded-xl border border-background-200/60 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={saveEdit} className="flex-1 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}